import { KeyValue } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  DrawerParams,
  DrawerSetting,
  GridColumn,
  PaginationSetting,
  SortEvent,
} from '@grid/abstractions';
import { TranslateService } from '@ngx-translate/core';
import { ModalSetting } from '@shared/abstraction';
import { ActionButton } from '@shared/abstraction/grid/action-button';
import { SearchSetting } from '@shared/abstraction/grid/search-setting';
import { Tooltip } from '@shared/abstraction/grid/tooltip';
import { paginationSettings } from '@shared/constants/pagination-settings';
import { CustomDataSource } from '@shared/mappers/custom-data-source';
import { GridRow } from '@shared/models/grid/grid-row';
import { ModalService } from '@shared/services/modal/modal.service';
import { get, set } from 'lodash-es';
import moment, { Moment, MomentInput } from 'moment';
import { QueryOptions } from 'odata-query';
import { Subscription } from 'rxjs';
import { DataType } from '../../../../helpers/enums/data-type';
import { SortType } from '../../../../helpers/enums/sort-type';

@Component({
  standalone: false,
  selector: 'gstar-grid',
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridComponent<TData, TOutputData> implements OnInit, OnDestroy {
  private readonly modalService = inject(ModalService);
  private readonly translateService = inject(TranslateService);

  @Input({ required: true }) dataIdKey!: keyof TData;
  @Input() headers: GridColumn[] = [];
  @Input() drawerSetting?: DrawerSetting<TData, TOutputData>;
  @Input() modalSetting?: ModalSetting<TData>;
  @Input() searchSetting?: SearchSetting<TData>;

  @Input() actionButtons: ActionButton<TData>[] = [];
  @Input() actionMenu: ActionButton<TData>[] = [];
  @Input() hasCheckboxButton!: boolean;
  @Input() noDataMessage!: string;
  @Input() noDataFilteredMessage!: string;
  @Input() tooltips!: Tooltip<TData>;

  @Input() lazy = false;
  @Input() idColumnToDisabled!: string;
  @Input() notAllowedColumnToEdit!: number[];

  @Input() set totalCount(value: number) {
    this.pagination = {
      ...this.pagination,
      totalItems: value,
    };
  }

  @Input() set source(dataSource: TData[]) {
    this.dataSource = dataSource;
    this.original = this.dataSource;
    this.seedClientSelectionCheckboxes();
  }

  @Input() query!: Partial<QueryOptions<TData>>;
  @Input() options: KeyValue<string, number>[] = [];
  @Input() showDrawer: boolean = true;
  @Output() queryChange = new EventEmitter<Partial<QueryOptions<TData>>>();
  @Output() selection = new EventEmitter<TData[]>();
  @Output() selectionAll = new EventEmitter<TData[]>();
  @Input() showToolbarDownloadButton = false;
  @Input() showToolbarDownloadAllButton = false;
  @Output() toolbarDownload = new EventEmitter<TData[]>();
  @Output() toolbarDownloadAll = new EventEmitter<void>();

  original: TData[] = [];
  dataSource: TData[] = [];
  actionsWidth?: number;
  selectAll: boolean = false;
  @Input() selectedItems: TData[] = [];
  pagination: PaginationSetting = paginationSettings;

  selectedDrawerType: DrawerParams<TData, TOutputData> | undefined;
  drawerModelChangeEmitter = new EventEmitter<{
    model: TOutputData;
    prevModel: TOutputData;
    isValid: boolean;
  }>();
  drawerModelChangeSubscription!: Subscription;

  selectedDrawerData:
    | {
        model: TOutputData;
        prevModel: TOutputData;
        isValid: boolean;
      }
    | undefined;
  filterIsEmpty: WritableSignal<boolean> = signal(false);
  isDataTableFirstLoading: WritableSignal<boolean> = signal(true);
  templateColumnType = DataType.Template;

  get hasCreateButton(): boolean {
    return !!this.drawerSetting?.create;
  }

  get hasEditButton(): boolean {
    return !!this.drawerSetting && !!this.drawerSetting.edit;
  }

  get hasDeleteButton(): boolean {
    return !!this.modalSetting && !!this.modalSetting.delete;
  }

  get hasViewButton(): boolean {
    return !!this.drawerSetting && !!this.drawerSetting.view;
  }

  get hasCustomButtons(): boolean {
    return !!this.actionButtons?.length;
  }

  get hasCustomMenu(): boolean {
    return !!this.actionMenu?.length;
  }

  isCheckboxColumn(header: GridColumn): boolean {
    return (
      header.dataType === DataType.Boolean &&
      !!header.renderBooleanAsCheckbox &&
      !!header.booleanCheckboxInteractive
    );
  }

  isReadOnlyBooleanCheckboxColumn(header: GridColumn): boolean {
    return (
      header.dataType === DataType.Boolean &&
      !!header.renderBooleanAsCheckbox &&
      !header.booleanCheckboxInteractive
    );
  }

  booleanCellValue(row: TData, key: string): boolean {
    return !!get(row as object, key);
  }

  allClientSelectedFor(key: string): boolean {
    return (
      this.dataSource.length > 0 &&
      this.dataSource.every(row => !!get(row as object, key))
    );
  }

  setClientBooleanSelection(row: TData, key: string, value: boolean): void {
    set(row as object, key, value);
  }

  toggleAllClientSelection(key: string, checked: boolean): void {
    this.dataSource.forEach(row => set(row as object, key, checked));
  }

  toGridRow(row: TData): GridRow<TData> {
    return {
      uuid: '',
      isChecked: false,
      isRowExpanded: false,
      isDisabledAccordionButton: false,
      data: row,
    };
  }

  toolbarDownloadClick(): void {
    const keys = this.headers
      .filter(h => h.renderBooleanAsCheckbox && h.booleanCheckboxInteractive)
      .map(h => h.key);
    const key = keys[0];
    if (!key) {
      this.toolbarDownload.emit([]);
      return;
    }
    const selected = this.dataSource.filter(row => !!get(row as object, key));
    this.toolbarDownload.emit(selected);
  }

  toolbarDownloadAllClick(): void {
    this.toolbarDownloadAll.emit();
  }

  private seedClientSelectionCheckboxes(): void {
    const keys = this.headers
      .filter(h => h.renderBooleanAsCheckbox && h.booleanCheckboxInteractive)
      .map(h => h.key);
    if (!keys.length) {
      return;
    }
    for (const row of this.dataSource) {
      for (const key of keys) {
        if (get(row as object, key) === undefined) {
          set(row as object, key, false);
        }
      }
    }
  }

  disabledButton(row: TData): boolean {
    if (this.dataIdKey === this.idColumnToDisabled) {
      return this.notAllowedColumnToEdit.includes(
        row[this.dataIdKey] as number
      );
    }
    return false;
  }

  constructor() {
    this.drawerModelChangeSubscription =
      this.drawerModelChangeEmitter.subscribe(changes => {
        this.selectedDrawerData = changes;
      });
  }

  ngOnInit(): void {
    if (this.drawerSetting) {
      this.actionsWidth = Object.entries(this.drawerSetting).length * 30;
    }
    this.noDataFilteredMessage =
      this.noDataFilteredMessage === ''
        ? this.translateService.instant(
            'admin.dashboard.notFoundRecordsMessage'
          )
        : '';
  }

  goToPage(pageNumber: number): void {
    this.pagination.currentPage = pageNumber;
  }

  ngOnDestroy(): void {
    this.drawerModelChangeSubscription?.unsubscribe();
    this.modalService.closeAll();
  }

  updatePage({ currentPage, rowByPage, totalItems }: PaginationSetting): void {
    this.pagination = {
      currentPage,
      rowByPage,
      totalItems,
    };

    this.query = {
      ...this.query,
      top: this.pagination.rowByPage,
      skip:
        (this.pagination.currentPage === 0
          ? 0
          : this.pagination.currentPage - 1) * this.pagination.rowByPage,
    };

    if (!this.lazy) {
      const from =
        this.pagination.currentPage * this.pagination.rowByPage -
        this.pagination.rowByPage;
      const end = this.pagination.currentPage * this.pagination.rowByPage;
      this.dataSource = this.dataSource.slice(from, end);
    }

    this.selectedItems = [];
    this.selection.emit(this.selectedItems);
    this.queryChange.emit(this.query);
  }

  trackByFn(
    _index: number,
    row: Record<string, string | number>
  ): string | number {
    return row[this.dataIdKey];
  }

  search(event: { originEvent: unknown; queryStr: string }): void {
    this.pagination.currentPage = 1;
    this.filterIsEmpty.set(event.queryStr === '' ? true : false);
    this.isDataTableFirstLoading.set(false);
    this.query = {
      ...this.query,
      skip: 0,
      filter: {
        [this.searchSetting!.filtersLogicalOperator]:
          this.searchSetting!.filters.map(filter => ({
            [filter.dataKey]: {
              [filter.operator]: event.queryStr,
            },
          })),
      },
    };
    this.selectedItems = [];
    this.selection.emit(this.selectedItems);
    this.queryChange.emit(this.query);
  }

  sort(event: SortEvent): void {
    const column = this.headers.find(
      (column: GridColumn) => column.key === event.sortKey
    );
    if (column) {
      column.sortType = event.sortingPhase;

      let orderByVal: 'asc' | 'desc' | undefined;
      switch (event.sortingPhase) {
        case SortType.Asc:
          orderByVal = 'asc';
          break;
        case SortType.Desc:
          orderByVal = 'desc';
          break;
        default:
          orderByVal = undefined;
      }

      this.query = {
        ...this.query,
        orderBy: orderByVal
          ? `${(column.sortKey ?? column.key).replaceAll('.', '/')} ${orderByVal}`
          : undefined,
      };

      this.selectedItems = [];
      this.selection.emit(this.selectedItems);
      this.queryChange.emit(this.query);

      if (!this.lazy) {
        this.sortJSONArray(column);
      }
    }
  }

  private ascendentSort(
    first: TData,
    second: TData,
    options: GridColumn
  ): number {
    if (options.dataType === DataType.String) {
      const f = get(first, options.key),
        s = get(second, options.key);

      return f === s ? 0 : f > s ? 1 : -1;
    } else if (options.dataType === DataType.Date) {
      const a: Moment = moment(
          first[options.key as keyof MomentInput] as string
        ),
        b: Moment = moment(second[options.key as keyof MomentInput] as string);
      return a.isAfter(b) ? 1 : -1;
    }
    return (
      parseFloat(first[options.key as keyof TData] as string) -
      parseFloat(second[options.key as keyof TData] as string)
    );
  }

  private descendentSort(
    first: TData,
    second: TData,
    options: GridColumn
  ): number {
    if (options.dataType === DataType.String) {
      const f = first[options.key as keyof MomentInput] as string,
        s = second[options.key as keyof MomentInput] as string;

      return f === s ? 0 : f < s ? 1 : -1;
    } else if (options.dataType === DataType.Date) {
      const a: Moment = moment(
          first[options.key as keyof MomentInput] as string
        ),
        b: Moment = moment(second[options.key as keyof MomentInput] as string);
      return b.isAfter(a) ? 1 : -1;
    }
    return (
      parseFloat(second[options.key as keyof TData] as string) -
      parseFloat(first[options.key as keyof TData] as string)
    );
  }

  sortJSONArray(column: GridColumn): void {
    let sortedJSONArray = this.dataSource;
    if (column.sortType === SortType.Asc) {
      sortedJSONArray = this.dataSource.sort((first: TData, second: TData) =>
        this.ascendentSort(first, second, column)
      );
    } else if (column.sortType === SortType.Desc) {
      sortedJSONArray = this.dataSource.sort((first: TData, second: TData) =>
        this.descendentSort(first, second, column)
      );
    } else {
      sortedJSONArray = this.original;
    }
    this.dataSource = [...sortedJSONArray];
  }

  viewModalHandler(_row: TData): void {
    if (this.drawerSetting?.view) {
      this.modalService.open(this.drawerSetting.view.component, {
        appModalId: 'view-modal',
      });
    }
  }

  createActionClick(): void {
    if (this.drawerSetting?.create) {
      this.selectedDrawerType = this.drawerSetting!.create;
      this.drawerSetting.create.componentInputs = {
        modelChange: this.drawerModelChangeEmitter,
        isEdit: false,
      };
    }
  }

  editActionClick(row: TData): void {
    if (this.drawerSetting?.edit) {
      this.selectedDrawerType = this.drawerSetting.edit;
      this.drawerSetting.edit.componentInputs = {
        model: row,
        modelChange: this.drawerModelChangeEmitter,
        isEdit: true,
      };
    }
  }

  deleteModalHandler(row: TData): void {
    if (this.modalSetting?.delete) {
      this.modalService.open(
        this.modalSetting.delete.component,
        {
          appModalId: 'delete-modal',
        },
        {
          title: this.modalSetting.delete.title,
          data: JSON.stringify(row),
          confirmationFn: () => this.modalSetting!.delete!.confirmationFn(row),
        }
      );
    }
  }

  drawerCloseClick(): void {
    this.selectedDrawerType = undefined;
    this.selectedDrawerData = undefined;
  }

  toggleAllSelections(selectAll: boolean): void {
    if (selectAll) {
      this.selectedItems = this.dataSource;
    } else {
      this.selectedItems = [];
    }
    this.selectionAll.emit(this.selectedItems);
  }

  onSelectionChange(row: TData): void {
    const isCheckedItem = (row as CustomDataSource).isChecked;
    const isAlreadyOnList = this.selectedItems.some(data => data === row);
    if (isCheckedItem && isAlreadyOnList) return;
    if (isCheckedItem) {
      this.selectedItems.push(row);
    } else {
      this.selectedItems = this.selectedItems.filter(data => data !== row);
    }
    this.selection.emit(this.selectedItems);
  }

  resetPagination(): void {
    this.pagination.currentPage = 1;
    this.updatePage(this.pagination);
  }
}
